/**
 * src/hooks/useUpdateChecker.js
 *
 * Checks GitHub for pull requests that are queued to come into the current environment.
 *
 *  - Development: no indicator (dev is source of truth)
 *  - Test:        watches open PRs targeting 'test'  (changes coming from development)
 *  - Production:  watches open PRs targeting 'main'  (changes coming from test)
 *
 * Requires VITE_GITHUB_TOKEN env var for private repos. Falls back gracefully.
 */

import { useState, useEffect } from 'react';

const REPO = 'ecurity-msk/seasuite';
const GH = 'https://api.github.com';

function detectEnv() {
  const h = window.location.hostname.toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1' || h.includes('dev.environment.'))
    return 'development';
  if (h.includes('test.environment.')) return 'test';
  return 'production';
}

export function useUpdateChecker() {
  const [pendingPRs, setPendingPRs] = useState([]);
  const [checked, setChecked] = useState(false);

  const env = detectEnv();

  useEffect(() => {
    if (env === 'development') {
      setChecked(true);
      return;
    }

    const targetBranch = env === 'test' ? 'test' : 'main';

    async function fetch() {
      try {
        const token = import.meta.env.VITE_GITHUB_TOKEN;
        if (!token) {
          setChecked(true);
          return;
        } // private repo — skip without a token

        const res = await window.fetch(
          `${GH}/repos/${REPO}/pulls?state=open&base=${targetBranch}&per_page=20`,
          { headers: { Accept: 'application/vnd.github+json', Authorization: `Bearer ${token}` } }
        );
        if (res.ok) {
          const prs = await res.json();
          setPendingPRs(Array.isArray(prs) ? prs : []);
        }
      } catch {
        // Silently fail — don't break the UI
      } finally {
        setChecked(true);
      }
    }

    fetch();
  }, [env]);

  return {
    env,
    pendingCount: pendingPRs.length,
    pendingPRs,
    hasUpdates: pendingPRs.length > 0,
    checked,
  };
}
